import React, { useState, useEffect, useRef, useCallback } from "react";
import { BrowserRouter as Router, useLocation, useNavigate } from "react-router-dom";
import { ThemeProvider, createTheme, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";

// Icons
import TerminalIcon from '@mui/icons-material/Terminal';
import FolderIcon from '@mui/icons-material/Folder';
import ContactPageIcon from '@mui/icons-material/ContactPage';
import ArticleIcon from '@mui/icons-material/Article';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import InfoIcon from '@mui/icons-material/Info';
import WifiIcon from '@mui/icons-material/Wifi';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import MemoryIcon from '@mui/icons-material/Memory';
import StorageIcon from '@mui/icons-material/Storage';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ComputerIcon from '@mui/icons-material/Computer';

import GitHubIcon from '@mui/icons-material/GitHub';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import XIcon from '@mui/icons-material/X';
import MinimizeIcon from '@mui/icons-material/Remove';
import MaximizeIcon from '@mui/icons-material/CropSquare';
import CloseIcon from '@mui/icons-material/Close';

// Local Components
import Terminal from "./components/Terminal";
import Projects from "./components/Projects";
import Certifications from "./components/Certifications";
import Blogs from "./components/Blogs";
import Contacts from "./components/Contacts";
import Experience from "./components/Experience";

// Centralized data

// Background Image
import kaliWallpaper from "./components/images/kali_wallpaper.jpg";

// Custom theme with Kali style
const kaliTheme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#00a2ff", // Kali Cyan/Blue
      light: "#33b5ff",
      dark: "#0072b3",
    },
    secondary: {
      main: "#ef4444", // Vulnerability / Root Red
      light: "#f87171",
      dark: "#b91c1c",
    },
    background: {
      default: "#080b10", // Kali Dark Slate
      paper: "#0d1017",   // Card/Window background
    },
    text: {
      primary: "#f3f4f6",
      secondary: "#9ca3af",
    },
  },
  typography: {
    fontFamily: "'Share Tech Mono', 'Outfit', 'Inter', monospace",
    h1: { fontWeight: 800 },
    h2: { fontWeight: 800 },
    h3: { fontWeight: 700 },
    h4: { fontWeight: 700 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          fontFamily: "'Share Tech Mono', monospace",
          textTransform: "none",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          border: "1px solid #1c2536",
          background: "#0d1017",
        },
      },
    },
  },
});

// Kali Dragon SVG Logo
function KaliDragonLogo({ color = "#00a2ff", size = 20 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ filter: "drop-shadow(0 0 4px " + color + "80)" }}
    >
      <path
        d="M12 2C11.5 3.5 10 5 8.5 6C7 7 5.5 8 4 9.5C2.5 11 2 12.5 2 14C2 17.5 5 20.5 8.5 21C9.5 21.1 10.5 21 11.5 20.5C12.5 20 13 19 13.5 18C14 17 14.5 16 15 15C16 13 17.5 11.5 19 10C20.5 8.5 21.5 7 22 5C20.5 5.5 19 6 17.5 6C16 6 14.5 5.5 13.5 5C12.5 4.5 12.2 3.2 12 2Z"
        fill={color}
      />
      <path
        d="M9 11C8.5 12 7.5 12.5 6.5 13C5.5 13.5 4.5 13.8 3.5 14C4.5 15.5 6 16.5 7.5 17C9 17.5 10.5 17.2 11.5 16.5C12.5 15.8 13 14.8 13.2 13.5C13.4 12.2 13 11.2 12.5 10.5C11.5 9.5 10 10 9 11Z"
        fill="#00e5ff"
        opacity="0.8"
      />
    </svg>
  );
}

// Window Wrapper Component
function Window({
  id,
  title,
  isOpen,
  isMinimized,
  isMaximized,
  x,
  y,
  w,
  h,
  zIndex,
  onClose,
  onMinimize,
  onMaximize,
  onFocus,
  onMove,
  icon: Icon,
  children
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [position, setPosition] = useState({ x, y });
  const [size, setSize] = useState({ w, h });
  const headerRef = useRef(null);
  const dragRef = useRef({ isDragging: false, startX: 0, startY: 0, startXPos: 0, startYPos: 0 });

  useEffect(() => {
    setPosition({ x, y });
  }, [x, y]);

  useEffect(() => {
    setSize({ w, h });
  }, [w, h]);

  if (!isOpen || isMinimized) return null;

  const handleMouseDown = (e) => {
    if (isMaximized || isMobile) return;
    onFocus(id);
    dragRef.current = {
      isDragging: true,
      startX: e.clientX,
      startY: e.clientY,
      startXPos: position.x,
      startYPos: position.y
    };
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleMouseMove = (e) => {
    if (!dragRef.current.isDragging) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    
    let newX = dragRef.current.startXPos + dx;
    let newY = dragRef.current.startYPos + dy;
    
    newY = Math.max(37, newY); // status bar height
    
    onMove(id, newX, newY);
  };

  const handleMouseUp = () => {
    dragRef.current.isDragging = false;
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
  };

  // Resizing handle
  const handleResizeMouseDown = (e) => {
    e.stopPropagation();
    onFocus(id);
    dragRef.current = {
      isResizing: true,
      startX: e.clientX,
      startY: e.clientY,
      startW: size.w,
      startH: size.h
    };
    document.addEventListener("mousemove", handleResizeMouseMove);
    document.addEventListener("mouseup", handleResizeMouseUp);
  };

  const handleResizeMouseMove = (e) => {
    if (!dragRef.current.isResizing) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    
    const newW = Math.max(400, dragRef.current.startW + dx);
    const newH = Math.max(300, dragRef.current.startH + dy);
    
    setSize({ w: newW, h: newH });
  };

  const handleResizeMouseUp = () => {
    dragRef.current.isResizing = false;
    document.removeEventListener("mousemove", handleResizeMouseMove);
    document.removeEventListener("mouseup", handleResizeMouseUp);
  };

  const winStyle = (isMaximized || isMobile)
    ? {
        position: "fixed",
        top: "36px",
        left: 0,
        width: "100vw",
        height: "calc(100vh - 36px)",
        zIndex,
        display: "flex",
        flexDirection: "column",
      }
    : {
        position: "absolute",
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: `${size.w}px`,
        height: `${size.h}px`,
        zIndex,
        display: "flex",
        flexDirection: "column",
      };

  return (
    <Paper
      elevation={12}
      onClick={() => onFocus(id)}
      sx={{
        ...winStyle,
        background: "#0c0f16",
        border: "1px solid #1c2331",
        borderRadius: (isMaximized || isMobile) ? 0 : "4px",
        overflow: "hidden",
        boxShadow: "0 12px 36px rgba(0, 0, 0, 0.7)",
      }}
    >
      {/* Window Title Bar */}
      <Box
        ref={headerRef}
        onMouseDown={handleMouseDown}
        onDoubleClick={() => !isMobile && onMaximize(id)}
        sx={{
          height: "32px",
          bgcolor: "#141923",
          borderBottom: "1px solid #1f283a",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 1.5,
          cursor: (isMaximized || isMobile) ? "default" : "move",
          userSelect: "none",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {Icon && <Icon sx={{ color: "#00a2ff", fontSize: "1.05rem" }} />}
          <Typography
            variant="body2"
            sx={{
              fontFamily: "'Share Tech Mono', monospace",
              fontWeight: 600,
              color: "#e5e7eb",
              fontSize: "0.82rem",
            }}
          >
            {title}
          </Typography>
        </Box>
        
        {/* Window controls */}
        <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
          {/* Minimize */}
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); onMinimize(id); }}
            sx={{
              width: "18px",
              height: "18px",
              p: 0,
              color: "#9ca3af",
              "&:hover": { bgcolor: "rgba(255,255,255,0.06)", color: "#00a2ff" }
            }}
          >
            <MinimizeIcon sx={{ fontSize: "0.85rem" }} />
          </IconButton>
          {/* Maximize (Hide on Mobile) */}
          {!isMobile && (
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onMaximize(id); }}
              sx={{
                width: "18px",
                height: "18px",
                p: 0,
                color: "#9ca3af",
                "&:hover": { bgcolor: "rgba(255,255,255,0.06)", color: "#00a2ff" }
              }}
            >
              <MaximizeIcon sx={{ fontSize: "0.75rem" }} />
            </IconButton>
          )}
          {/* Close */}
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); onClose(id); }}
            sx={{
              width: "18px",
              height: "18px",
              p: 0,
              color: "#f87171",
              "&:hover": { bgcolor: "#ef4444", color: "#ffffff" }
            }}
          >
            <CloseIcon sx={{ fontSize: "0.85rem" }} />
          </IconButton>
        </Box>
      </Box>

      {/* Window Contents */}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          p: isMobile ? 1.5 : 2,
          position: "relative",
          bgcolor: "#080b11",
        }}
      >
        {children}
      </Box>

      {/* Resize handle (Hide on Mobile or Maximized) */}
      {!isMaximized && !isMobile && (
        <Box
          onMouseDown={handleResizeMouseDown}
          sx={{
            position: "absolute",
            right: 0,
            bottom: 0,
            width: "10px",
            height: "10px",
            cursor: "se-resize",
            zIndex: 10,
          }}
        />
      )}
    </Paper>
  );
}

// Biography (whoami) window component
function BiographyView() {
  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4} sx={{ textAlign: "center" }}>
          <Box
            sx={{
              width: "120px",
              height: "120px",
              borderRadius: "50%",
              bgcolor: "#141923",
              border: "2px solid #00a2ff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2,
              boxShadow: "0 0 15px rgba(0, 162, 255, 0.4)",
            }}
          >
            <KaliDragonLogo size={70} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: "#ffffff", mb: 0.5 }}>
            Tathagata Roy
          </Typography>
          <Typography variant="subtitle2" sx={{ color: "#00a2ff", mb: 2, letterSpacing: "0.08em" }}>
            CYBERSECURITY ANALYST // WEB DEV
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "center", gap: 1.5 }}>
            <IconButton href="https://github.com/roy9495" target="_blank" size="small" sx={{ border: "1px solid #1f283a", color: "#ffffff" }}>
              <GitHubIcon fontSize="small" />
            </IconButton>
            <IconButton href="https://www.linkedin.com/in/roy9495/" target="_blank" size="small" sx={{ border: "1px solid #1f283a", color: "#00a2ff" }}>
              <LinkedInIcon fontSize="small" />
            </IconButton>
            <IconButton href="https://x.com/royzen9495" target="_blank" size="small" sx={{ border: "1px solid #1f283a", color: "#00a2ff" }}>
              <XIcon fontSize="small" />
            </IconButton>
          </Box>
        </Grid>
        <Grid item xs={12} md={8}>
          <Typography variant="h6" sx={{ color: "#00a2ff", mb: 1.5, borderBottom: "1px solid #1f283a", pb: 0.5 }}>
            [>] System Profile
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.6, color: "text.secondary", mb: 3 }}>
            I am a Cloud Security Analyst and Web Developer specializing in cloud-native security mappings, vulnerability reviews, malware research audits, and interactive high-performance frontends. I build secure solutions by bridging engineering concepts with threat-mitigation frameworks (NIST, CIS Benchmarks).
          </Typography>

          <Typography variant="h6" sx={{ color: "#00a2ff", mb: 1.5, borderBottom: "1px solid #1f283a", pb: 0.5 }}>
            [>] Core Skills
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {["Pentesting", "Malware Analysis", "GRC / ISMS", "NIST / CIS Benchmarks", "Cloud Security", "React.js", "Python / Go / Rust", "Docker & CI/CD", "OWASP Top 10"].map((s) => (
              <Chip
                key={s}
                label={s}
                size="small"
                sx={{
                  bgcolor: "rgba(0, 162, 255, 0.08)",
                  border: "1px solid rgba(0, 162, 255, 0.2)",
                  color: "#00a2ff",
                  fontFamily: "'Share Tech Mono', monospace",
                  fontWeight: 600,
                  fontSize: "0.75rem",
                }}
              />
            ))}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

// Main Desktop Layout
function DesktopShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [currentTime, setCurrentTime] = useState("");
  const [conkyStats, setConkyStats] = useState({ cpu: 12, ram: 4.8 });
  const [powerDialogOpen, setPowerDialogOpen] = useState(false);

  // Initial Window States
  const [windows, setWindows] = useState({
    terminal: { id: "terminal", title: "qterminal - root@kali:~", isOpen: true, isMinimized: false, isMaximized: false, x: 60, y: 80, w: 720, h: 450, zIndex: 10 },
    biography: { id: "biography", title: "biography - root@kali:~/bio", isOpen: true, isMinimized: false, isMaximized: false, x: 120, y: 110, w: 750, h: 480, zIndex: 11 },
    projects: { id: "projects", title: "projects - root@kali:~/projects", isOpen: false, isMinimized: false, isMaximized: false, x: 160, y: 140, w: 850, h: 540, zIndex: 1 },
    certifications: { id: "certifications", title: "certifications - root@kali:~/certs", isOpen: false, isMinimized: false, isMaximized: false, x: 200, y: 170, w: 850, h: 500, zIndex: 1 },
    blogs: { id: "blogs", title: "blogs - root@kali:~/blogs", isOpen: false, isMinimized: false, isMaximized: false, x: 240, y: 200, w: 800, h: 500, zIndex: 1 },
    experience: { id: "experience", title: "experience - root@kali:~/experience", isOpen: false, isMinimized: false, isMaximized: false, x: 280, y: 230, w: 850, h: 550, zIndex: 1 },
    contact: { id: "contact", title: "secure-contact - root@kali:~/contact", isOpen: false, isMinimized: false, isMaximized: false, x: 320, y: 260, w: 750, h: 530, zIndex: 1 },
  });

  // Track ticking clock
  useEffect(() => {
    const updateTime = () => {
      const d = new Date();
      const options = { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true };
      setCurrentTime(d.toLocaleTimeString(undefined, options));
    };
    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  // CPU/RAM oscillation for realistic "Conky" widget
  useEffect(() => {
    const interval = setInterval(() => {
      setConkyStats({
        cpu: Math.max(2, Math.min(98, Math.round(15 + (Math.random() - 0.5) * 12))),
        ram: parseFloat((4.7 + Math.random() * 0.2).toFixed(1)),
      });
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  // Drag and focus window controllers
  const focusWindow = useCallback((id) => {
    setWindows((prev) => {
      const maxZ = Math.max(...Object.values(prev).map((w) => w.zIndex || 0), 10);
      if (prev[id].zIndex === maxZ && maxZ > 10) return prev;
      return {
        ...prev,
        [id]: { ...prev[id], zIndex: maxZ + 1 },
      };
    });
  }, []);

  const openWindow = useCallback((id) => {
    setWindows((prev) => {
      const maxZ = Math.max(...Object.values(prev).map((w) => w.zIndex || 0), 10);
      return {
        ...prev,
        [id]: { ...prev[id], isOpen: true, isMinimized: false, zIndex: maxZ + 1 },
      };
    });
    // Synchronize router paths
    const routeMap = {
      biography: "/whoami",
      projects: "/projects",
      certifications: "/certifications",
      blogs: "/blogs",
      experience: "/experience",
      contact: "/contact",
    };
    if (routeMap[id]) {
      navigate(routeMap[id]);
    }
  }, [navigate]);

  const closeWindow = useCallback((id) => {
    setWindows((prev) => ({
      ...prev,
      [id]: { ...prev[id], isOpen: false },
    }));
    const routeMap = {
      biography: "/whoami",
      projects: "/projects",
      certifications: "/certifications",
      blogs: "/blogs",
      experience: "/experience",
      contact: "/contact",
    };
    if (location.pathname === routeMap[id]) {
      navigate("/");
    }
  }, [location.pathname, navigate]);

  const minimizeWindow = useCallback((id) => {
    setWindows((prev) => ({
      ...prev,
      [id]: { ...prev[id], isMinimized: true },
    }));
  }, []);

  const maximizeWindow = useCallback((id) => {
    setWindows((prev) => ({
      ...prev,
      [id]: { ...prev[id], isMaximized: !prev[id].isMaximized },
    }));
  }, []);

  const moveWindow = useCallback((id, x, y) => {
    setWindows((prev) => ({
      ...prev,
      [id]: { ...prev[id], x, y },
    }));
  }, []);

  // Listen to path changes and open/focus corresponding window
  useEffect(() => {
    const p = location.pathname;
    if (p === "/projects") openWindow("projects");
    else if (p === "/certifications") openWindow("certifications");
    else if (p === "/blogs") openWindow("blogs");
    else if (p === "/experience") openWindow("experience");
    else if (p === "/contact") openWindow("contact");
    else if (p === "/whoami") openWindow("biography");
  }, [location.pathname, openWindow]);

  const handleMenuOpen = (e) => {
    setMenuAnchor(e.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const triggerApplicationMenu = (id) => {
    openWindow(id);
    handleMenuClose();
  };

  const handleResetDesktop = () => {
    // Reset all windows
    setWindows({
      terminal: { id: "terminal", title: "qterminal - root@kali:~", isOpen: true, isMinimized: false, isMaximized: false, x: 60, y: 80, w: 720, h: 450, zIndex: 10 },
      biography: { id: "biography", title: "biography - root@kali:~/bio", isOpen: false, isMinimized: false, isMaximized: false, x: 120, y: 110, w: 750, h: 480, zIndex: 1 },
      projects: { id: "projects", title: "projects - root@kali:~/projects", isOpen: false, isMinimized: false, isMaximized: false, x: 160, y: 140, w: 850, h: 540, zIndex: 1 },
      certifications: { id: "certifications", title: "certifications - root@kali:~/certs", isOpen: false, isMinimized: false, isMaximized: false, x: 200, y: 170, w: 850, h: 500, zIndex: 1 },
      blogs: { id: "blogs", title: "blogs - root@kali:~/blogs", isOpen: false, isMinimized: false, isMaximized: false, x: 240, y: 200, w: 800, h: 500, zIndex: 1 },
      experience: { id: "experience", title: "experience - root@kali:~/experience", isOpen: false, isMinimized: false, isMaximized: false, x: 280, y: 230, w: 850, h: 550, zIndex: 1 },
      contact: { id: "contact", title: "secure-contact - root@kali:~/contact", isOpen: false, isMinimized: false, isMaximized: false, x: 320, y: 260, w: 750, h: 530, zIndex: 1 },
    });
    setPowerDialogOpen(false);
    navigate("/");
  };

  const desktopIcons = [
    { id: "biography", label: "whoami.txt", icon: InfoIcon, color: "#00a2ff" },
    { id: "projects", label: "projects", icon: FolderIcon, color: "#e0a96d" },
    { id: "certifications", label: "certifications", icon: FolderIcon, color: "#e0a96d" },
    { id: "blogs", label: "blogs", icon: FolderIcon, color: "#e0a96d" },
    { id: "experience", label: "experience", icon: FolderIcon, color: "#e0a96d" },
    { id: "contact", label: "contact_portal.sh", icon: ContactPageIcon, color: "#f87171" },
    { id: "terminal", label: "qterminal.desktop", icon: TerminalIcon, color: "#50fa7b" },
  ];

  return (
    <Box
      sx={{
        width: "100vw",
        height: "100vh",
        backgroundImage: `url(${kaliWallpaper})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* 1. XFCE Panel (Top Status Bar) */}
      <Box
        sx={{
          height: "36px",
          bgcolor: "#0f131a",
          borderBottom: "1px solid #1a2233",
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 1.5,
          boxShadow: "0 2px 10px rgba(0,0,0,0.5)",
        }}
      >
        {/* Left Side: Kali Applications Menu Launcher + Quick Launch */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton
            size="small"
            onClick={handleMenuOpen}
            sx={{
              p: 0.5,
              borderRadius: "4px",
              bgcolor: menuAnchor ? "rgba(0,162,255,0.15)" : "transparent",
              "&:hover": { bgcolor: "rgba(0,162,255,0.2)" },
            }}
          >
            <KaliDragonLogo size={22} />
          </IconButton>
          
          <Typography
            variant="body2"
            sx={{
              fontFamily: "'Share Tech Mono', monospace",
              fontWeight: "bold",
              color: "#e5e7eb",
              mr: 1.5,
              userSelect: "none",
            }}
          >
            Applications
          </Typography>

          {/* Quick launch panel */}
          <Box sx={{ display: { xs: "none", sm: "flex" }, gap: 0.5, borderLeft: "1px solid #1f283a", pl: 1 }}>
            <IconButton size="small" onClick={() => openWindow("terminal")} title="Terminal Emulator" sx={{ color: "#50fa7b" }}>
              <TerminalIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => openWindow("biography")} title="Biography (whoami)" sx={{ color: "#00a2ff" }}>
              <InfoIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => openWindow("projects")} title="File Manager / Projects" sx={{ color: "#e0a96d" }}>
              <FolderIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Right Side: Tray Icons, Time, Battery, Shutdown */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.8 }}>
          {/* Virtual Desktops Workspace Switcher */}
          <Box
            sx={{
              display: { xs: "none", sm: "flex" },
              border: "1px solid #1f283a",
              borderRadius: "3px",
              overflow: "hidden",
            }}
          >
            {["1", "2"].map((wk) => (
              <Box
                key={wk}
                sx={{
                  px: 0.8,
                  py: 0.1,
                  fontSize: "0.75rem",
                  bgcolor: wk === "1" ? "rgba(0,162,255,0.15)" : "transparent",
                  color: wk === "1" ? "#00a2ff" : "#9ca3af",
                  fontFamily: "'Share Tech Mono', monospace",
                  cursor: "pointer",
                }}
              >
                {wk}
              </Box>
            ))}
          </Box>

          {/* System Indicators */}
          <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", gap: 1, color: "#9ca3af" }}>
            <WifiIcon sx={{ fontSize: "1.1rem", color: "#50fa7b" }} />
            <VolumeUpIcon sx={{ fontSize: "1.1rem" }} />
            <BatteryChargingFullIcon sx={{ fontSize: "1.1rem", color: "#22c55e" }} />
            <NotificationsIcon sx={{ fontSize: "1.1rem" }} />
          </Box>

          {/* Real Time Clock */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              borderLeft: { xs: "none", sm: "1px solid #1f283a" },
              pl: { xs: 0, sm: 1.5 },
            }}
          >
            <AccessTimeIcon sx={{ fontSize: "1rem", color: "#00a2ff" }} />
            <Typography
              variant="body2"
              sx={{
                fontFamily: "'Share Tech Mono', monospace",
                fontWeight: 600,
                color: "#e5e7eb",
                fontSize: "0.82rem",
              }}
            >
              {currentTime}
            </Typography>
          </Box>

          {/* User / Power Menu */}
          <Box sx={{ borderLeft: "1px solid #1f283a", pl: 1, display: "flex", alignItems: "center" }}>
            <Chip
              label="root"
              size="small"
              sx={{
                bgcolor: "rgba(239, 68, 68, 0.1)",
                color: "#ef4444",
                border: "1px solid rgba(239, 68, 68, 0.25)",
                fontSize: "0.7rem",
                fontWeight: 750,
                mr: 1,
                display: { xs: "none", sm: "inline-flex" }
              }}
            />
            <IconButton size="small" onClick={() => setPowerDialogOpen(true)} sx={{ color: "#ef4444" }} title="Power Off / Log Out">
              <PowerSettingsNewIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Applications Dropdown Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            background: "#0c0f16",
            border: "1px solid #1c2536",
            borderRadius: "4px",
            minWidth: "260px",
            mt: 0.8,
            boxShadow: "0 6px 20px rgba(0,0,0,0.6)",
          },
        }}
      >
        <Box sx={{ px: 2, py: 1, borderBottom: "1px solid #1a2233", mb: 1 }}>
          <Typography variant="body2" sx={{ fontFamily: "'Share Tech Mono', monospace", fontWeight: "bold", color: "#00a2ff" }}>
            [ KALI PORTFOLIO MENU ]
          </Typography>
        </Box>
        
        <MenuItem onClick={() => triggerApplicationMenu("biography")} sx={{ display: "flex", justifyContent: "space-between", py: 1 }}>
          <Typography variant="body2" sx={{ fontFamily: "'Share Tech Mono', monospace", color: "#f3f4f6" }}>
            01. Info Gathering (Biography)
          </Typography>
          <InfoIcon sx={{ color: "#00a2ff", fontSize: "1.1rem" }} />
        </MenuItem>

        <MenuItem onClick={() => triggerApplicationMenu("projects")} sx={{ display: "flex", justifyContent: "space-between", py: 1 }}>
          <Typography variant="body2" sx={{ fontFamily: "'Share Tech Mono', monospace", color: "#f3f4f6" }}>
            02. Web Assessment (Projects)
          </Typography>
          <FolderIcon sx={{ color: "#e0a96d", fontSize: "1.1rem" }} />
        </MenuItem>

        <MenuItem onClick={() => triggerApplicationMenu("certifications")} sx={{ display: "flex", justifyContent: "space-between", py: 1 }}>
          <Typography variant="body2" sx={{ fontFamily: "'Share Tech Mono', monospace", color: "#f3f4f6" }}>
            03. Credentials (Certs)
          </Typography>
          <WorkspacePremiumIcon sx={{ color: "#a855f7", fontSize: "1.1rem" }} />
        </MenuItem>

        <MenuItem onClick={() => triggerApplicationMenu("blogs")} sx={{ display: "flex", justifyContent: "space-between", py: 1 }}>
          <Typography variant="body2" sx={{ fontFamily: "'Share Tech Mono', monospace", color: "#f3f4f6" }}>
            04. Threat Intel (Blogs)
          </Typography>
          <ArticleIcon sx={{ color: "#00e5ff", fontSize: "1.1rem" }} />
        </MenuItem>

        <MenuItem onClick={() => triggerApplicationMenu("experience")} sx={{ display: "flex", justifyContent: "space-between", py: 1 }}>
          <Typography variant="body2" sx={{ fontFamily: "'Share Tech Mono', monospace", color: "#f3f4f6" }}>
            05. Audits (Work Experience)
          </Typography>
          <BusinessCenterIcon sx={{ color: "#3b82f6", fontSize: "1.1rem" }} />
        </MenuItem>

        <MenuItem onClick={() => triggerApplicationMenu("contact")} sx={{ display: "flex", justifyContent: "space-between", py: 1 }}>
          <Typography variant="body2" sx={{ fontFamily: "'Share Tech Mono', monospace", color: "#f3f4f6" }}>
            06. Secure Channel (Contact)
          </Typography>
          <ContactPageIcon sx={{ color: "#f87171", fontSize: "1.1rem" }} />
        </MenuItem>

        <MenuItem onClick={() => triggerApplicationMenu("terminal")} sx={{ borderTop: "1px solid #1a2233", mt: 1, py: 1, display: "flex", justifyContent: "space-between" }}>
          <Typography variant="body2" sx={{ fontFamily: "'Share Tech Mono', monospace", color: "#50fa7b", fontWeight: "bold" }}>
            07. Shell Terminal (qterminal)
          </Typography>
          <TerminalIcon sx={{ color: "#50fa7b", fontSize: "1.1rem" }} />
        </MenuItem>
      </Menu>

      {/* 2. Desktop Environment Space */}
      <Box
        sx={{
          position: "absolute",
          top: "36px",
          left: 0,
          right: 0,
          bottom: 0,
          p: 3,
          boxSizing: "border-box",
        }}
      >
        {/* Desktop Icons column */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flexWrap: "wrap",
            gap: 2.5,
            width: "120px",
            height: "100%",
            alignContent: "flex-start",
            zIndex: 5,
            position: "relative",
          }}
        >
          {desktopIcons.map((icon) => {
            const IconComponent = icon.icon;
            const isWindowOpen = windows[icon.id]?.isOpen;
            return (
              <Box
                key={icon.id}
                onClick={() => openWindow(icon.id)}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "90px",
                  p: 1,
                  borderRadius: "4px",
                  cursor: "pointer",
                  bgcolor: isWindowOpen ? "rgba(0,162,255,0.08)" : "transparent",
                  border: isWindowOpen ? "1px dashed rgba(0,162,255,0.25)" : "1px solid transparent",
                  transition: "all 0.2s",
                  "&:hover": {
                    bgcolor: "rgba(0, 162, 255, 0.15)",
                    borderColor: "rgba(0,162,255,0.4)",
                  },
                }}
              >
                <IconComponent
                  sx={{
                    fontSize: "2.4rem",
                    color: icon.color,
                    mb: 0.8,
                    filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.5))",
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: "'Share Tech Mono', monospace",
                    color: "#f3f4f6",
                    fontSize: "0.78rem",
                    textAlign: "center",
                    textShadow: "0px 1px 4px #000, 0px 1px 4px #000, 0px 2px 8px #000",
                    wordBreak: "break-all",
                    lineHeight: 1.1,
                  }}
                >
                  {icon.label}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Floating "Conky" System Monitoring Widget (Right Hand Desktop) */}
        <Paper
          elevation={4}
          sx={{
            position: "absolute",
            top: 24,
            right: 24,
            width: "280px",
            bgcolor: "rgba(10, 15, 24, 0.65)",
            backdropFilter: "blur(8px)",
            border: "1px solid rgba(0, 162, 255, 0.15)",
            borderRadius: "4px",
            p: 2,
            fontFamily: "'Share Tech Mono', monospace",
            color: "#e5e7eb",
            zIndex: 1,
            display: { xs: "none", md: "block" },
            userSelect: "none",
            pointerEvents: "none",
          }}
        >
          <Box sx={{ borderBottom: "1px solid rgba(0,162,255,0.3)", pb: 1, mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
            <ComputerIcon sx={{ color: "#00a2ff", fontSize: "1.15rem" }} />
            <Typography variant="body2" sx={{ fontWeight: "bold", letterSpacing: "0.08em", color: "#00a2ff" }}>
              conky@tathagata
            </Typography>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2, fontSize: "0.8rem" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <span>SYSTEM:</span>
              <span style={{ color: "#ffffff" }}>Kali GNU/Linux</span>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <span>UPTIME:</span>
              <span style={{ color: "#ffffff" }}>2h 14m</span>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <span>SHELL:</span>
              <span style={{ color: "#50fa7b" }}>zsh 5.9</span>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <span>SEC STATUS:</span>
              <span style={{ color: "#50fa7b", fontWeight: "bold" }}>SECURE</span>
            </Box>
            
            <Box sx={{ mt: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <MemoryIcon sx={{ fontSize: "0.9rem", color: "#00a2ff" }} />
                  <span>CPU:</span>
                </Box>
                <span>{conkyStats.cpu}%</span>
              </Box>
              <Box sx={{ height: "4px", bgcolor: "#1f283a", borderRadius: "2px", overflow: "hidden" }}>
                <Box sx={{ height: "100%", width: `${conkyStats.cpu}%`, bgcolor: conkyStats.cpu > 80 ? "#ef4444" : "#00a2ff", transition: "width 0.5s ease" }} />
              </Box>
            </Box>

            <Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <StorageIcon sx={{ fontSize: "0.9rem", color: "#00a2ff" }} />
                  <span>RAM:</span>
                </Box>
                <span>{conkyStats.ram}GB / 16GB</span>
              </Box>
              <Box sx={{ height: "4px", bgcolor: "#1f283a", borderRadius: "2px", overflow: "hidden" }}>
                <Box sx={{ height: "100%", width: `${(conkyStats.ram / 16) * 100}%`, bgcolor: "#00a2ff", transition: "width 0.5s ease" }} />
              </Box>
            </Box>
          </Box>
        </Paper>

        {/* 3. Window Manager Instances */}
        {Object.values(windows).map((win) => {
          let ContentComponent = null;
          switch (win.id) {
            case "terminal":
              ContentComponent = <Terminal openWindow={openWindow} />;
              break;
            case "biography":
              ContentComponent = <BiographyView />;
              break;
            case "projects":
              ContentComponent = <Projects />;
              break;
            case "certifications":
              ContentComponent = <Certifications />;
              break;
            case "blogs":
              ContentComponent = <Blogs />;
              break;
            case "experience":
              ContentComponent = <Experience />;
              break;
            case "contact":
              ContentComponent = <Contacts />;
              break;
            default:
              ContentComponent = null;
          }

          return (
            <Window
              key={win.id}
              {...win}
              onClose={closeWindow}
              onMinimize={minimizeWindow}
              onMaximize={maximizeWindow}
              onFocus={focusWindow}
              onMove={moveWindow}
              icon={
                win.id === "terminal" ? TerminalIcon :
                win.id === "biography" ? InfoIcon :
                win.id === "contact" ? ContactPageIcon : FolderIcon
              }
            >
              {ContentComponent}
            </Window>
          );
        })}
      </Box>

      {/* 4. Power Dialog Modal (Log out prompt) */}
      <Dialog
        open={powerDialogOpen}
        onClose={() => setPowerDialogOpen(false)}
        PaperProps={{
          sx: {
            background: "#0c0f16",
            border: "1px solid #ef444450",
            borderRadius: "4px",
            boxShadow: "0 0 25px rgba(239, 68, 68, 0.2)",
            minWidth: "300px",
          },
        }}
      >
        <DialogTitle sx={{ color: "#ef4444", fontFamily: "'Share Tech Mono', monospace", fontWeight: "bold" }}>
          [!] System Interruption Menu
        </DialogTitle>
        <DialogContent sx={{ color: "text.secondary", fontFamily: "'Share Tech Mono', monospace", fontSize: "0.9rem" }}>
          Terminate all active desktop configurations and reload the secure shell?
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setPowerDialogOpen(false)} sx={{ color: "#9ca3af" }}>
            Cancel
          </Button>
          <Button onClick={handleResetDesktop} variant="contained" color="secondary">
            Restart Shell
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// Router root wrapper
function App() {
  return (
    <ThemeProvider theme={kaliTheme}>
      <CssBaseline />
      <Router>
        <DesktopShell />
      </Router>
    </ThemeProvider>
  );
}

export default App;
